import type { Position } from '@sorare/core/src/__generated__/globalTypes';
import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import { useDragAndDrop } from '#src/hooks/useDragAndDrop.ts';

export type SwapSlot = {
  lineupId: string;
  slotIndex: number;
};

type SeasonBonusRuleContext = {
  seasonBonusRuleMin?: number;
  seasonBonusRuleMax?: number;
  inSeasonEligible?: boolean;
  lineupInSeasonEligible?: boolean[];
};

type SlotAverageScoreContext = {
  averageScore?: number;
  isMVP?: boolean;
};

type SharedSwapRuleContext = SeasonBonusRuleContext & {
  maxIncomingAverageScore?: number;
  lineupAverageScores?: number[];
  averageScoresCap?: number;
  sumOfAverageScoresCap?: number;
  immuneAppearances?: number;
};

type AverageScoreRuleContext = SlotAverageScoreContext & SharedSwapRuleContext;

export type SwapPayload = SwapSlot &
  AverageScoreRuleContext & {
    positions: Position[];
    leagueSlug: string;
  };

type DragContext = {
  lineupId?: string;
  leagueSlug?: string;
  onSwap?: (source: SwapSlot, target: SwapSlot) => void;
  draggedPayload: SwapPayload | null;
  setDraggedPayload: (payload: SwapPayload | null) => void;
  onDragCapture?: () => void;
};

type DragPropsInput = AverageScoreRuleContext & {
  canBeSwapped: boolean;
  isSwapping: boolean;
  cardPositions?: Position[];
  slotIndex: number;
};

type DropHandlersInput = SharedSwapRuleContext & {
  canBeSwapped: boolean;
  appearanceRulePositions?: Position[];
  targetAverageScore?: number;
  targetIsMVP?: boolean;
  slotIndex: number;
};

type TargetPayloadInput = Omit<
  DropHandlersInput,
  'canBeSwapped' | 'appearanceRulePositions'
> & {
  lineupId?: string;
  leagueSlug?: string;
  positions?: Position[] | null;
};

const isRuleMatch = (cardPositions: Position[], rulePositions?: Position[]) => {
  if (!rulePositions?.length) return true;
  return rulePositions.some(position => cardPositions.includes(position));
};

const normalizeAverageScore = (score?: number) => score ?? 0;

const countInSeasonEligibleCards = (eligibilityBySlot: boolean[]) =>
  eligibilityBySlot.filter(Boolean).length;

const respectsSeasonBonusRuleBounds = ({
  inSeasonEligibleCardsCount,
  min,
  max,
}: {
  inSeasonEligibleCardsCount: number;
  min?: number;
  max?: number;
}) => {
  if (
    typeof min === 'number' &&
    Number.isFinite(min) &&
    inSeasonEligibleCardsCount < min
  ) {
    return false;
  }
  if (
    typeof max === 'number' &&
    Number.isFinite(max) &&
    inSeasonEligibleCardsCount > max
  ) {
    return false;
  }
  return true;
};

const isSeasonBonusRuleEligible = ({
  sourcePayload,
  targetPayload,
}: {
  sourcePayload: SwapPayload;
  targetPayload: SwapPayload;
}) => {
  const shouldCheckSeasonBonusRule =
    typeof sourcePayload.seasonBonusRuleMin === 'number' ||
    typeof sourcePayload.seasonBonusRuleMax === 'number' ||
    typeof targetPayload.seasonBonusRuleMin === 'number' ||
    typeof targetPayload.seasonBonusRuleMax === 'number';
  if (!shouldCheckSeasonBonusRule) return true;

  const sourceLineup = sourcePayload.lineupInSeasonEligible;
  const targetLineup = targetPayload.lineupInSeasonEligible;
  if (!sourceLineup?.length || !targetLineup?.length) {
    return true;
  }

  const sourceCardInSeasonEligible = sourcePayload.inSeasonEligible ?? false;
  const targetCardInSeasonEligible = targetPayload.inSeasonEligible ?? false;

  if (sourcePayload.lineupId === targetPayload.lineupId) {
    const nextLineupInSeasonEligible = [...sourceLineup];
    nextLineupInSeasonEligible[sourcePayload.slotIndex] =
      targetCardInSeasonEligible;
    nextLineupInSeasonEligible[targetPayload.slotIndex] =
      sourceCardInSeasonEligible;
    const inSeasonEligibleCardsCount = countInSeasonEligibleCards(
      nextLineupInSeasonEligible
    );
    return respectsSeasonBonusRuleBounds({
      inSeasonEligibleCardsCount,
      min: targetPayload.seasonBonusRuleMin,
      max: targetPayload.seasonBonusRuleMax,
    });
  }

  const nextSourceLineupInSeasonEligible = [...sourceLineup];
  const nextTargetLineupInSeasonEligible = [...targetLineup];
  nextSourceLineupInSeasonEligible[sourcePayload.slotIndex] =
    targetCardInSeasonEligible;
  nextTargetLineupInSeasonEligible[targetPayload.slotIndex] =
    sourceCardInSeasonEligible;

  const sourceInSeasonEligibleCardsCount = countInSeasonEligibleCards(
    nextSourceLineupInSeasonEligible
  );
  const targetInSeasonEligibleCardsCount = countInSeasonEligibleCards(
    nextTargetLineupInSeasonEligible
  );

  const sourceEligible = respectsSeasonBonusRuleBounds({
    inSeasonEligibleCardsCount: sourceInSeasonEligibleCardsCount,
    min: sourcePayload.seasonBonusRuleMin,
    max: sourcePayload.seasonBonusRuleMax,
  });
  const targetEligible = respectsSeasonBonusRuleBounds({
    inSeasonEligibleCardsCount: targetInSeasonEligibleCardsCount,
    min: targetPayload.seasonBonusRuleMin,
    max: targetPayload.seasonBonusRuleMax,
  });
  return sourceEligible && targetEligible;
};

const isIncomingAverageScoreEligible = ({
  incomingAverageScore,
  maxIncomingAverageScore,
}: {
  incomingAverageScore?: number;
  maxIncomingAverageScore?: number;
}) => {
  if (typeof maxIncomingAverageScore !== 'number') return true;
  if (typeof incomingAverageScore !== 'number') {
    return false;
  }
  const isEligible = incomingAverageScore <= maxIncomingAverageScore;
  return isEligible;
};

const getCappedTotal = ({
  scores,
  immuneAppearances,
  sumAllScores = false,
}: {
  scores: number[];
  immuneAppearances: number;
  sumAllScores?: boolean;
}) => {
  if (sumAllScores) {
    return scores.reduce((acc, score) => acc + score, 0);
  }

  return [...scores]
    .sort((a, b) => b - a)
    .slice(Math.max(0, immuneAppearances))
    .reduce((acc, score) => acc + score, 0);
};

const respectsAverageScoresCap = ({
  scores,
  cap,
  immuneAppearances = 0,
  sumAllScores = false,
}: {
  scores: number[];
  cap?: number;
  immuneAppearances?: number;
  sumAllScores?: boolean;
}) => {
  if (typeof cap !== 'number') return true;
  return getCappedTotal({ scores, immuneAppearances, sumAllScores }) <= cap;
};

const respectsAverageScoresCaps = ({
  scores,
  averageScoresCap,
  sumOfAverageScoresCap,
  immuneAppearances,
}: {
  scores: number[];
  averageScoresCap?: number;
  sumOfAverageScoresCap?: number;
  immuneAppearances?: number;
}) => {
  return (
    respectsAverageScoresCap({
      scores,
      cap: averageScoresCap,
      immuneAppearances,
    }) &&
    respectsAverageScoresCap({
      scores,
      cap: sumOfAverageScoresCap,
      sumAllScores: true,
    })
  );
};

const hasAverageScoreCapRule = ({
  averageScoresCap,
  sumOfAverageScoresCap,
}: {
  averageScoresCap?: number;
  sumOfAverageScoresCap?: number;
}) => {
  return (
    typeof averageScoresCap === 'number' ||
    typeof sumOfAverageScoresCap === 'number'
  );
};

const toTargetSwapPayload = ({
  lineupId,
  leagueSlug,
  positions,
  slotIndex,
  targetAverageScore,
  seasonBonusRuleMin,
  seasonBonusRuleMax,
  inSeasonEligible,
  lineupInSeasonEligible,
  maxIncomingAverageScore,
  targetIsMVP,
  lineupAverageScores,
  averageScoresCap,
  sumOfAverageScoresCap,
  immuneAppearances,
}: TargetPayloadInput): SwapPayload => ({
  lineupId: lineupId || '',
  slotIndex,
  positions: positions || [],
  leagueSlug: leagueSlug || '',
  seasonBonusRuleMin,
  seasonBonusRuleMax,
  inSeasonEligible,
  lineupInSeasonEligible,
  averageScore: targetAverageScore,
  maxIncomingAverageScore,
  isMVP: targetIsMVP,
  lineupAverageScores,
  averageScoresCap,
  sumOfAverageScoresCap,
  immuneAppearances,
});

const isAverageScoreSwapEligible = ({
  sourcePayload,
  targetPayload,
}: {
  sourcePayload: SwapPayload;
  targetPayload: SwapPayload;
}) => {
  const respectsIncomingSlotCaps =
    isIncomingAverageScoreEligible({
      incomingAverageScore: sourcePayload.averageScore,
      maxIncomingAverageScore: targetPayload.maxIncomingAverageScore,
    }) &&
    isIncomingAverageScoreEligible({
      incomingAverageScore: targetPayload.averageScore,
      maxIncomingAverageScore: sourcePayload.maxIncomingAverageScore,
    });

  const shouldCheckAverageScoreRules =
    hasAverageScoreCapRule({
      averageScoresCap: sourcePayload.averageScoresCap,
      sumOfAverageScoresCap: sourcePayload.sumOfAverageScoresCap,
    }) ||
    hasAverageScoreCapRule({
      averageScoresCap: targetPayload.averageScoresCap,
      sumOfAverageScoresCap: targetPayload.sumOfAverageScoresCap,
    });

  // Without average-score rules, the per-slot incoming cap is our only guard.
  if (!shouldCheckAverageScoreRules) return respectsIncomingSlotCaps;
  if (sourcePayload.isMVP && targetPayload.isMVP) return true;

  const sourceScores = sourcePayload.lineupAverageScores;
  const targetScores = targetPayload.lineupAverageScores;
  if (!sourceScores?.length || !targetScores?.length) {
    // Fallback to the per-slot cap check when we cannot simulate lineup totals.
    return respectsIncomingSlotCaps;
  }

  if (sourcePayload.lineupId === targetPayload.lineupId) {
    const nextScores = [...sourceScores];
    const sourceScore = normalizeAverageScore(sourcePayload.averageScore);
    const destinationScore = normalizeAverageScore(targetPayload.averageScore);
    nextScores[sourcePayload.slotIndex] = destinationScore;
    nextScores[targetPayload.slotIndex] = sourceScore;
    return respectsAverageScoresCaps({
      scores: nextScores,
      averageScoresCap: targetPayload.averageScoresCap,
      sumOfAverageScoresCap: targetPayload.sumOfAverageScoresCap,
      immuneAppearances: targetPayload.immuneAppearances,
    });
  }

  const nextSourceScores = [...sourceScores];
  const nextTargetScores = [...targetScores];
  nextSourceScores[sourcePayload.slotIndex] = normalizeAverageScore(
    targetPayload.averageScore
  );
  nextTargetScores[targetPayload.slotIndex] = normalizeAverageScore(
    sourcePayload.averageScore
  );

  const sourceEligible = respectsAverageScoresCaps({
    scores: nextSourceScores,
    averageScoresCap: sourcePayload.averageScoresCap,
    sumOfAverageScoresCap: sourcePayload.sumOfAverageScoresCap,
    immuneAppearances: sourcePayload.immuneAppearances,
  });
  const targetEligible = respectsAverageScoresCaps({
    scores: nextTargetScores,
    averageScoresCap: targetPayload.averageScoresCap,
    sumOfAverageScoresCap: targetPayload.sumOfAverageScoresCap,
    immuneAppearances: targetPayload.immuneAppearances,
  });
  return sourceEligible && targetEligible;
};

export const useLineupDragAndDrop = ({
  onSwap,
  lineupId,
  leagueSlug,
  draggedPayload,
  setDraggedPayload,
  onDragCapture,
}: DragContext) => {
  const { sport } = useSportContext();
  const {
    getDragProps: getBaseDragProps,
    getDropHandlers: getBaseDropHandlers,
    endDrag,
  } = useDragAndDrop<SwapPayload>({
    draggedPayload,
    setDraggedPayload,
    hideDraggedElement: false,
    onDragCapture,
  });
  const draggedSwapPayload = draggedPayload;
  const draggedPositions = draggedSwapPayload?.positions || null;
  const draggedLeagueSlug = draggedSwapPayload?.leagueSlug || null;
  const bypassPositionRules = sport === Sport.NBA;

  const isLeagueEligible =
    !draggedLeagueSlug || draggedLeagueSlug === leagueSlug;

  const getDragProps = ({
    canBeSwapped,
    isSwapping,
    cardPositions,
    seasonBonusRuleMin,
    seasonBonusRuleMax,
    inSeasonEligible,
    lineupInSeasonEligible,
    averageScore,
    maxIncomingAverageScore,
    isMVP,
    lineupAverageScores,
    averageScoresCap,
    sumOfAverageScoresCap,
    immuneAppearances,
    slotIndex,
  }: DragPropsInput) => {
    if (!onSwap || !lineupId || !canBeSwapped || isSwapping) return undefined;
    if (!cardPositions?.length) return undefined;
    if (!leagueSlug) return undefined;

    return getBaseDragProps({
      isDraggable: true,
      payload: {
        lineupId,
        slotIndex,
        positions: cardPositions,
        leagueSlug,
        seasonBonusRuleMin,
        seasonBonusRuleMax,
        inSeasonEligible,
        lineupInSeasonEligible,
        averageScore,
        maxIncomingAverageScore,
        isMVP,
        lineupAverageScores,
        averageScoresCap,
        sumOfAverageScoresCap,
        immuneAppearances,
      },
    });
  };

  const getDropHandlers = ({
    canBeSwapped,
    appearanceRulePositions,
    seasonBonusRuleMin,
    seasonBonusRuleMax,
    inSeasonEligible,
    lineupInSeasonEligible,
    targetAverageScore,
    maxIncomingAverageScore,
    targetIsMVP,
    lineupAverageScores,
    averageScoresCap,
    sumOfAverageScoresCap,
    immuneAppearances,
    slotIndex,
  }: DropHandlersInput) => {
    const isTargetRuleEligible =
      bypassPositionRules ||
      !draggedPositions ||
      isRuleMatch(draggedPositions, appearanceRulePositions);
    const isRuleEligible = isTargetRuleEligible;
    const targetPayload = toTargetSwapPayload({
      lineupId,
      leagueSlug,
      positions: draggedPositions,
      slotIndex,
      targetAverageScore,
      seasonBonusRuleMin,
      seasonBonusRuleMax,
      inSeasonEligible,
      lineupInSeasonEligible,
      maxIncomingAverageScore,
      targetIsMVP,
      lineupAverageScores,
      averageScoresCap,
      sumOfAverageScoresCap,
      immuneAppearances,
    });
    const isAverageScoreEligible =
      !draggedSwapPayload ||
      isAverageScoreSwapEligible({
        sourcePayload: draggedSwapPayload,
        targetPayload,
      });
    const isSeasonBonusEligible =
      !draggedSwapPayload ||
      isSeasonBonusRuleEligible({
        sourcePayload: draggedSwapPayload,
        targetPayload,
      });
    const isDroppable = Boolean(
      onSwap &&
      lineupId &&
      canBeSwapped &&
      isRuleEligible &&
      isLeagueEligible &&
      isAverageScoreEligible &&
      isSeasonBonusEligible
    );
    return getBaseDropHandlers({
      isDroppable,
      onDrop: payload => {
        if (!onSwap || !lineupId || !canBeSwapped) return;
        if (
          !bypassPositionRules &&
          !isRuleMatch(payload.positions, appearanceRulePositions)
        ) {
          return;
        }
        if (payload.leagueSlug !== leagueSlug) return;
        if (
          !isAverageScoreSwapEligible({
            sourcePayload: payload,
            targetPayload,
          })
        ) {
          return;
        }
        if (
          !isSeasonBonusRuleEligible({
            sourcePayload: payload,
            targetPayload,
          })
        ) {
          return;
        }
        if (payload.lineupId === lineupId && payload.slotIndex === slotIndex)
          return;
        endDrag();
        onSwap(payload, { lineupId, slotIndex });
      },
    });
  };

  return { getDragProps, getDropHandlers, draggedSwapPayload };
};
